/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { BehaviorSubject, Subject, combineLatest, filter, first, map, startWith } from "rxjs";
import { ButtonComponent, SpinnerComponent } from "typedb-cloud-framework";
import { AsyncPipe, DatePipe, NgTemplateOutlet } from "@angular/common";
import { resourceAvailabilityStreamOf, PageScaffoldComponent } from "../../scaffold/page/page-scaffold.component";
import { ApplicationState } from "../../../service/application-state.service";
import { MatTooltipModule } from "@angular/material/tooltip";
import { clustersDeployPath, clustersPath, teamSettingsPath } from "../../../routing/resource-paths";
import { ClustersTableComponent } from "../../cluster/table/clusters-table.component";
import { TeamController } from "../../../service/team/team-controller.service";
import { clusterProperties } from "../../../concept/cluster";
import { animate, sequence, style, transition, trigger } from "@angular/animations";
import { Invoice } from "../../../concept/subscription";
import { mergeChanges } from "../../../concept/api-response";
import { SubscriptionApi } from "../../../service/subscription/subscription-api.service";
import { CurrencyPipe } from "../currency.pipe";
import { SanityService } from "../../../service/sanity.service";

@Component({
    selector: "tp-overview-page",
    templateUrl: "./overview-page.component.html",
    styleUrls: ["./overview-page.component.scss"],
    imports: [
        PageScaffoldComponent, AsyncPipe, RouterLink, ButtonComponent, MatTooltipModule, ClustersTableComponent,
        CurrencyPipe, DatePipe, SpinnerComponent, NgTemplateOutlet
    ],
    animations: [
        trigger('announcementLeave', [
            transition(':leave', [
                sequence([
                    animate('150ms linear', style({ opacity: 0 })),
                    animate('50ms linear', style({ height: 0, padding: 0 })),
                ])
            ]),
        ]),
    ]
})
export class OverviewPageComponent implements OnInit, OnDestroy {
    readonly team = this.app.requireCurrentTeam();
    readonly userFirstName$ = this.app.currentUser$.pipe(map((user) => user?.firstName));

    private readonly unsub$ = new Subject<void>();
    readonly clusters = this.teamCtl.clustersTable(this.unsub$, { hideActions: true, hideSelect: true });
    readonly displayedClusterProperties = [clusterProperties.id.id, clusterProperties.space.id, clusterProperties.clusterStatus.id, clusterProperties.backupFrequency.id];
    readonly availability$ = resourceAvailabilityStreamOf(this.clusters);
    readonly popupMenuItems = (x: any) => [];
    readonly upcomingInvoice$ = new BehaviorSubject<Invoice | null>(null);
    private readonly upcomingInvoiceUnsub$ = new Subject<void>();
    readonly hasPaymentMethod$ = combineLatest([
        this.subscriptionApi.listCreditCards(this.team.uuid),
        this.subscriptionApi.getMarketplaceAccounts(this.team.uuid)
    ]).pipe(
        map(([cards, marketplaceAccounts]) =>
            cards.length > 0 ||
            marketplaceAccounts.gcp?.status == "active" ||
            marketplaceAccounts.aws?.status == "active"
        ),
        startWith(true),
    );
    readonly announcement$ = this.sanity.announcement;
    readonly imageTransform$ = this.announcement$.pipe(map((x) => `rotate(${x.id.length * 72}deg)`));
    dismissedAnnouncement = this.app.getLastDismissedAnnouncement();

    protected readonly now = new Date();
    get greetingByTime(): string {
        const hours = this.now.getHours();
        if (hours >= 23 || hours < 3) return "Hello night owl";
        else if (hours >= 3 && hours < 12) return "Good morning";
        else if (hours >=12 && hours < 18) return "Good afternoon";
        else if (hours >= 18 && hours < 23) return "Good evening";
        else return "Good day";
    }
    get greetingSuffixByTime(): string {
        const hours = this.now.getHours();
        if (hours >= 23 || hours < 3) return "🦉";
        else return "";
    }

    ngOnInit() {
        this.clusters.displayedProperties = this.clusters.displayedProperties
            .filter(property => this.displayedClusterProperties.includes(property.id));
        this.initUpcomingInvoiceLoader();
    }

    constructor(
        public route: ActivatedRoute, protected app: ApplicationState, private teamCtl: TeamController,
        private subscriptionApi: SubscriptionApi, private sanity: SanityService,
    ) {  }

    ngOnDestroy() {
        this.unsub$.next();
    }

    initUpcomingInvoiceLoader() {
        this.app.hasTeamAdminAccess$.pipe(first(), filter((x) => x)).subscribe(() => {
            const team = this.app.requireCurrentTeam();
            this.subscriptionApi.getUpcomingInvoice(team.uuid, this.upcomingInvoiceUnsub$).subscribe((res) => {
                if (!res) {
                    this.upcomingInvoice$.next(null);
                    return;
                }
                const current = this.upcomingInvoice$.value;
                const mergeResult = mergeChanges(current, res);
                if (mergeResult.result === "shouldDelete") {
                    this.upcomingInvoiceUnsub$.next();
                } else if (mergeResult.result === "shouldResync") {
                    console.warn(`Received update is not valid in the current state; forcing a full resync`);
                    this.upcomingInvoiceUnsub$.next();
                    this.initUpcomingInvoiceLoader();
                    return;
                }
                if (mergeResult.data) {
                    this.upcomingInvoice$.next(mergeResult.data);
                }
            });
        });
    }

    dismissAnnouncement(announcementID: string) {
        this.dismissedAnnouncement = announcementID;
        this.app.setLastDismissedAnnouncement(announcementID);
    }

    protected readonly clustersDeployPath = clustersDeployPath;
    protected readonly clustersPath = clustersPath;
    protected readonly teamSettingsPath = teamSettingsPath;
}
